import { DataSource } from '@angular/cdk/collections';
import {
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  DestroyRef,
  effect,
  inject,
  input,
  OnDestroy,
  OnInit,
  output,
  signal,
  Signal,
  WritableSignal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { TranslocoService } from '@jsverse/transloco';
import { NgbPopoverModule } from '@ng-bootstrap/ng-bootstrap';
import { saveAs } from 'file-saver-es';
import moment from 'moment';
import { BehaviorSubject, Observable } from 'rxjs';

import { AppSharedModule } from 'app/app-shared.module';
import { SimpleDialogButtonColor } from 'app/components/simple-dialog.component';
import { CollectionModule } from 'app/modules/collection/collection.module';
import { DocumentViewOptions } from 'app/modules/collection/components/document/document-view/document-view.component';
import { AuthService } from 'app/providers/bridge-api/auth.service';
import { RealtimeConnection, RealtimeMessage } from 'app/providers/realtime.service';
import { ConfigurationItem } from 'app/providers/semantria-api/configuration.service';
import { DocumentAnalysis } from 'app/providers/storage-api/analysis.service';
import {
  ConfigurationTesterService,
  CTEvaluationMetrics,
  CTEvaluationResponse,
  CTEvaluationResultExportFormat,
  CTEvaluationResultExtended,
  CTEvaluationResultStatus,
} from 'app/providers/storage-api/configurations.service';
import { Document } from 'app/providers/storage-api/document.service';
import { StorageListResult, StoragePageRequest } from 'app/providers/storage-api.service';
import { AlertService } from 'app/services/alert.service';
import { DialogService } from 'app/services/dialog.service';
import {
  EnableComponentTelemetry,
  injectComponentTelemetryService,
} from 'app/services/otel.service';
import { TimezoneService } from 'app/services/timezone.service';
import { ConfigurationDetailsTabBase, Highlight } from '../configuration-details-tab-base';

export interface CTDocumentListOptions {}

export type CTEvaluationResultStatusInfo = Record<
  CTEvaluationResultStatus,
  { icon: string; color: string }
>;

export interface CTPage<T> {
  content: T[];
  totalElements: number;
  size: number;
  number: number;
}

export interface CTPageRequest {
  offset: number;
  limit: number;
  searchText?: string;
  statuses?: string[];
}

export type CTEndpoint<T> = (req: CTPageRequest) => Promise<T>;

class CTDocumentDataSource extends DataSource<CTEvaluationResultExtended> {
  private _allItems: CTEvaluationResultExtended[] = [];
  private _items = new BehaviorSubject<CTEvaluationResultExtended[]>([]);
  private _metrics = new BehaviorSubject<CTEvaluationMetrics>(null);
  private _loading = new BehaviorSubject<boolean>(false);
  private _error = new BehaviorSubject<any>(null);
  private _page = 0;
  private _pageSize = 100;
  private _hasData: boolean;
  private _noMoreData: boolean;
  private _filterStatuses: string[] = [];
  private _currentSearchText: string = '';
  private _availableStatuses = new BehaviorSubject<string[]>([]);
  private _projectId: string;
  private _collectionName: string;

  readonly metrics$ = this._metrics.asObservable();
  readonly loading$ = this._loading.asObservable();
  readonly error$ = this._error.asObservable();
  readonly availableStatuses$ = this._availableStatuses.asObservable();

  private _displayColumns = new BehaviorSubject<string[]>(['documentId', 'text', 'status']);
  readonly displayColumns$ = this._displayColumns.asObservable();

  get displayColumns(): string[] {
    return this._displayColumns.value;
  }

  updateDisplayColumns(columns: string[]) {
    this._displayColumns.next(columns);
  }

  constructor(
    private endpoint: CTEndpoint<CTEvaluationResponse>,
    private enchanceResultsFunction: (docs: CTEvaluationResultExtended[]) => void
  ) {
    super();
  }

  connect(): Observable<readonly CTEvaluationResultExtended[]> {
    return this._items.asObservable();
  }

  disconnect(): void {
    this._items.complete();
    this._metrics.complete();
  }

  set pageSize(size: number) {
    this._pageSize = size;
  }

  get pageSize(): number {
    return this._pageSize;
  }

  get hasData(): boolean {
    return this._hasData;
  }

  get canFetchNext(): boolean {
    return this._hasData && !this._noMoreData;
  }

  get metrics(): CTEvaluationMetrics {
    return this._metrics.getValue();
  }

  get availableStatuses(): string[] {
    return this._availableStatuses.getValue();
  }

  get projectId(): string {
    return this._projectId;
  }

  get collectionName(): string {
    return this._collectionName;
  }

  private updateAvailableStatusesFromMetrics(metrics: CTEvaluationMetrics) {
    const statuses: string[] = [];
    if (metrics?.hits > 0) {
      statuses.push('HIT');
    }
    if (metrics?.added > 0) {
      statuses.push('ADDED');
    }
    if (metrics?.removed > 0) {
      statuses.push('REMOVED');
    }
    if (metrics?.unchanged > 0) {
      statuses.push('UNCHANGED');
    }
    if (metrics?.different > 0) {
      statuses.push('DIFFERENT_HIT');
    }
    if (metrics?.errors > 0) {
      statuses.push('ERROR');
    }
    if (metrics?.missed > 0) {
      statuses.push('DIDNT_HIT');
    }
    this._availableStatuses.next(statuses);
  }

  updateFilter(statuses: string[]) {
    this._filterStatuses = statuses ?? [];
    // Trigger a new search with the updated filter
    this.fetchFirstPage(this._currentSearchText);
  }

  // Method to set filter without triggering fetch
  setFilterWithoutFetch(statuses: string[]) {
    this._filterStatuses = statuses ?? [];
  }

  // Getter for current filter statuses
  get filterStatuses(): string[] {
    return this._filterStatuses;
  }

  // Getter for current search text
  get currentSearchText(): string {
    return this._currentSearchText;
  }

  async fetch(page: number, searchText?: string, statuses?: string[]) {
    try {
      this._loading.next(true);
      this._page = page;
      this._currentSearchText = searchText ?? '';
      const request = {
        offset: this._page * this.pageSize,
        limit: this.pageSize,
        searchText: searchText ? searchText.trim() : '',
        statuses: statuses ?? this._filterStatuses,
      };
      const response = await this.endpoint(request);
      this.handleResponse(response);
    } catch (e) {
      this.handleError(e);
    } finally {
      this._loading.next(false);
    }
  }

  async fetchFirstPage(searchText?: string) {
    this.reset();
    this.fetch(0, searchText, this._filterStatuses);
  }

  async fetchNextPage(searchText?: string) {
    this.fetch(++this._page, searchText, this._filterStatuses);
  }

  private reset() {
    this._page = 0;
    this._hasData = undefined;
    this._noMoreData = undefined;
    this._allItems = [];
    this._items.next([]);
    this._metrics.next(null);
  }

  private handleResponse(response: CTEvaluationResponse) {
    // Enchance results
    this.enchanceResultsFunction(response?.results);

    // Merge results
    this._allItems = [...this._allItems, ...(response?.results ?? [])];
    this._hasData = this._allItems?.length > 0;
    this._noMoreData = response?.results?.length < this._pageSize;
    this._items.next(this._allItems);

    // Must fetch on first page
    if (this._page === 0) {
      let metrics = null;
      if (response) {
        metrics = {
          hasComparison: response.hasComparison,
          docCount: response.docCount,
          hits: response.hits,
          added: response.added,
          removed: response.removed,
          unchanged: response.unchanged,
          different: response.different,
          errors: response.errors,
          expirationDate: response.expirationDate,
          hasSections: response.hasSections,
          missed: response.missed,
        };
        this._projectId = response.projectId;
        this._collectionName = response.collectionName;
        // Update available statuses from metrics
        this.updateAvailableStatusesFromMetrics(metrics);
      }
      if (metrics?.hasSections) {
        this.updateDisplayColumns(['documentId', 'text', 'status', 'section']);
      }
      this._metrics.next(metrics);
    }
  }

  private handleError(e: any) {
    this._error.next(e);
    this._items.next([]);
  }
}

@Component({
  selector: 'app-configuration-tester-document-list',
  imports: [AppSharedModule, NgbPopoverModule, CollectionModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './configuration-tester-document-list.component.html',
  styleUrl: './configuration-tester-document-list.component.scss',
})
export class ConfigurationTesterDocumentListComponent implements OnInit, OnDestroy {
  private readonly EXPIRATION_DATE_TIME_FORMAT = 'YYYY-MM-DD HH:mm';
  readonly DOCUMENTS_MODAL_PAGE_SIZE: number = 20;
  readonly VIRTUAL_SCROLL_ITEM_SIZE: number = 50;
  readonly RESULT_STATUS_INFO: CTEvaluationResultStatusInfo = {
    HIT: { icon: 'circle_check_filled', color: 'success' },
    DIDNT_HIT: { icon: 'cancel_circle_filled', color: 'error' },
    ADDED: { icon: 'add_circle_filled', color: 'success' },
    REMOVED: { icon: 'cancel_circle_filled', color: 'error' },
    DIFFERENT_HIT: { icon: 'circle_check_filled', color: 'success' },
    UNCHANGED: { icon: 'circle_check_filled', color: 'success' },
    ERROR: { icon: 'document_alert', color: 'error' },
  };

  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();

  private _destroyRef = inject(DestroyRef);
  private _timezone: string;
  private _initialized = false;

  options = input<CTDocumentListOptions>();
  selectedItem = input<ConfigurationItem>();
  selectedItemChanged = input<boolean>();
  isDisabled = input<boolean>();
  isEvaluationInProgress = input<boolean>();
  highlightTextFunction =
    input<
      (
        doc: DocumentAnalysis,
        documentText: string,
        itemValues: string[],
        lookInSections?: boolean,
        sectionId?: string
      ) => string
    >();
  highlightValueFunction = input<(item) => string[]>();
  statusFilter = input<string[]>();

  metricsChanged = output<CTEvaluationMetrics>();
  resultsCleared = output<boolean>();
  resultsArrived = output<CTEvaluationResponse>();
  availableStatuses = output<string[]>();
  fullscreenToggled = output<void>();

  private isInternalLoading: WritableSignal<boolean> = signal<boolean>(false);
  private isDatasourceLoading: WritableSignal<boolean> = signal<boolean>(false);
  showDocumentViewModal: WritableSignal<boolean> = signal<boolean>(false);
  documentViewModalOptions: WritableSignal<DocumentViewOptions> = signal<DocumentViewOptions>(null);
  isFullscreen = input<boolean>(false);
  isLoading: Signal<boolean> = computed(() => {
    return this.isDatasourceLoading() || this.isEvaluationInProgress() || this.isInternalLoading();
  });

  expirationDate: string;
  dataSource: CTDocumentDataSource;
  searchText: string;
  realtimeHandlers: RealtimeConnection[] = [];
  selectedStatusFilter: string[] = [];
  availableStatusOptions: string[] = [];

  constructor(
    private configurationTesterService: ConfigurationTesterService,
    private timezoneService: TimezoneService,
    private translocoService: TranslocoService,
    private dialogService: DialogService,
    private alertService: AlertService,
    private authService: AuthService
  ) {
    effect(() => {
      const filter = this.statusFilter();

      if (!this.dataSource || !this._initialized) {
        return;
      }

      this.dataSource.updateFilter(filter);
    });
  }

  async ngOnInit(): Promise<void> {
    this._timezone = this.timezoneService.timezone;

    // Add focus listener for fullscreen scroll fix
    window.addEventListener('focus', this.onWindowFocus.bind(this));
    this.dataSource = new CTDocumentDataSource(
      (request) =>
        this.configurationTesterService.getResults(
          this.selectedItem().id,
          request.offset,
          request.limit,
          request.searchText,
          request.statuses
        ),
      this.enchanceResults.bind(this)
    );
    this.dataSource.metrics$
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((metrics) => this.setExpirationDate(metrics?.expirationDate));
    this.dataSource.loading$
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((isLoading) => this.isDatasourceLoading.set(isLoading));
    this.dataSource.error$.pipe(takeUntilDestroyed(this._destroyRef)).subscribe((error) => {
      if (error) {
        this.alertService.showErrorToast(
          this.translocoService.translate(
            'configuration-tester-document-list-component.failed-to-load-result'
          ),
          error
        );
      }
    });
    this.dataSource.availableStatuses$
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((statuses) => {
        this.availableStatusOptions = statuses;
        this.availableStatuses.emit(statuses);
      });

    if (this.statusFilter()) {
      this.dataSource.setFilterWithoutFetch(this.statusFilter());
    }

    await this.dataSource.fetch(0, undefined, this.dataSource.filterStatuses);

    this.realtimeHandlers = [
      this.configurationTesterService.on(this.authService.currentUserId, this.onEvent.bind(this)),
    ];

    this._initialized = true;
  }

  ngOnDestroy(): void {
    this.realtimeHandlers.forEach((handler) => {
      if (handler) {
        handler.disconnect();
      }
    });

    // Remove focus listener
    window.removeEventListener('focus', this.onWindowFocus.bind(this));

    this._initialized = false;
  }

  get trimmedSearchText() {
    return this.searchText?.trim()?.length > 0 ? this.searchText.trim() : '';
  }

  onWindowFocus(): void {
    // When window regains focus in fullscreen mode, ensure scroll events work
    if (this.isFullscreen()) {
      setTimeout(() => {
        const viewportElement = document.querySelector(
          '.cdk-virtual-scroll-viewport'
        ) as HTMLElement;
        if (viewportElement) {
          // Force a scroll check to ensure events are properly bound
          viewportElement.dispatchEvent(new Event('scroll'));
        }
      }, 100);
    }
  }

  onTableScroll(event: any) {
    // For CDK Virtual Scroll Viewport, we need to get the element reference differently
    let target = event.target || event.currentTarget || event.srcElement;

    // If we're in fullscreen mode, try to get the virtual scroll viewport element
    if (this.isFullscreen()) {
      const viewportElement = document.querySelector('.cdk-virtual-scroll-viewport');
      if (viewportElement) {
        target = viewportElement;
      }
    }

    const scrollTop = target.scrollTop;
    const scrollHeight = target.scrollHeight;
    const offsetHeight = target.offsetHeight;

    if (
      scrollHeight - (scrollTop + offsetHeight) < this.VIRTUAL_SCROLL_ITEM_SIZE &&
      !this.isLoading() &&
      this.dataSource.canFetchNext
    ) {
      this.dataSource.fetchNextPage(this.trimmedSearchText);
    }
  }

  onDocumentViewModalClosed() {
    this.showDocumentViewModal.set(false);
  }

  private onEvent(data: RealtimeMessage<CTEvaluationResponse>) {
    const completed = data?.object?.status === 'COMPLETED';
    const error = data?.object?.error;

    if (completed) {
      this.dataSource.fetchFirstPage();
      this.alertService.showToast(
        this.translocoService.translateObject(
          'configuration-tester-document-list-component.evaluation-finished'
        )
      );
    } else if (error) {
      this.alertService.showErrorToast(
        this.translocoService.translateObject(
          'configuration-tester-document-list-component.evaluation-failed'
        ),
        { message: error }
      );
    }
    this.resultsArrived.emit(data?.object);
  }

  async openDocument(item: CTEvaluationResultExtended) {
    const sectioned = !!item.sectionName;
    const pageSupplier: (
      request?: StoragePageRequest<Document>
    ) => Promise<StorageListResult<Document>> = async (request) => {
      const referenceDocumentId = request.params['reference-document-id'] as string;
      const pageNumber = (request.params['page-number'] ?? 0) as number;
      const pageSize = (request.params['page-size'] ?? this.DOCUMENTS_MODAL_PAGE_SIZE) as number;
      const pageOffset = pageNumber * pageSize;

      // Get grouped resutls first
      const groupedResults = await this.configurationTesterService.getGroupedResults(
        this.selectedItem().id,
        pageOffset,
        pageSize,
        referenceDocumentId,
        this.dataSource.currentSearchText,
        this.dataSource.filterStatuses
      );
      // Convert grouped results to documents
      const documents = groupedResults.items.map((groupItem) =>
        this.configurationTesterService.convertEvaluationResponseToDocument(groupItem)
      );

      // Return documents as a page
      return Promise.resolve({
        count: groupedResults.count,
        pageSize: groupedResults.pageSize,
        pageIndex: groupedResults.pageIndex,
        items: documents,
      });
    };
    const pageItemSupplier: (documentId: string) => Promise<Document> = async (
      documentId: string
    ) => {
      // Get result (all documents by documentId) for the selected document
      const resultsByItemId = await this.configurationTesterService.getResult(
        this.selectedItem().id,
        documentId
      );
      // Convert result to document
      const document = this.configurationTesterService.convertEvaluationResponseToDocument({
        projectId: resultsByItemId.projectId,
        collectionName: resultsByItemId.collectionName,
        documentId,
        hasSections: resultsByItemId.hasSections,
        results: resultsByItemId.results,
      });
      return document;
    };

    try {
      // Start loading
      this.isInternalLoading.set(true);
      // Fetch initial page for the selected document
      const groupedResults = await pageSupplier({
        endpoint: 'not-used',
        params: {
          'reference-document-id': item.documentId,
        },
      });
      // All documents on the same page as the selected document
      const documents = groupedResults.items;
      // Index of the selected document
      const documentIndex = documents.findIndex((doc) => doc.id === item.documentId);
      // Page of documents
      const page: StorageListResult<Document> = {
        count: groupedResults.count,
        pageSize: groupedResults.pageSize,
        items: documents,
      };
      // Page index (if the user clicks on an item that is not on the first page, the page index is not 0)
      const pageIndex = groupedResults.pageIndex;
      const partialDocumentViewOptions: Partial<DocumentViewOptions> = {
        collectionName: this.dataSource.collectionName,
        projectId: this.dataSource.projectId,
        page,
        pageSupplier,
        pageItemSupplier,
        pageIndex,
        documentIndex,
        showNlpData: false,
        showMetadata: false,
        limit: this.DOCUMENTS_MODAL_PAGE_SIZE,
        documentId: item.documentId,
        sectionFilters: sectioned
          ? [
              {
                documentId: item.documentId,
                sectionId: +item.semantriaSectionId,
              },
            ]
          : undefined,
        isDashboardLocked: true,
        hideOpenNewTabAction: true,
        hideRightSplitArea: true,
      };
      this.documentViewModalOptions.set(partialDocumentViewOptions as DocumentViewOptions);
      this.showDocumentViewModal.set(true);
    } catch (err) {
      this.alertService.showErrorToast(
        this.translocoService.translateObject(
          'configuration-tester-document-list-component.open-document-failed'
        ),
        err
      );
    } finally {
      this.isInternalLoading.set(false);
    }
  }

  setExpirationDate(expirationDate: Date) {
    this.expirationDate = moment
      .tz(expirationDate, this._timezone)
      .format(this.EXPIRATION_DATE_TIME_FORMAT);
  }

  enchanceResults(docs: CTEvaluationResultExtended[]) {
    docs?.forEach((doc) => {
      let highlightedText: string;

      // Use new highlights format if available
      if (doc.highlights && doc.highlights.length > 0) {
        const highlights: Highlight[] = doc.highlights.map((h) => ({
          offset: h.offset,
          length: h.length,
          offsetType: 'char' as const,
        }));
        highlightedText = this.highlightTextStringFromHighlights(highlights, doc.sourceText);
      }

      // Apply highlighted text
      doc.highlightedSourceText = highlightedText;

      // Apply status info
      if (doc.status) {
        const statusInfo = this.RESULT_STATUS_INFO[doc.status];
        doc.icon = statusInfo?.icon;
        doc.color = statusInfo?.color;
      }
    });
  }

  private highlightTextStringFromHighlights(highlights: Highlight[], documentText: string): string {
    return ConfigurationDetailsTabBase.prototype.highlightTextStringFromHighlights.call(
      this,
      highlights,
      documentText
    );
  }

  getStatusFilterButtonText(t: any): string {
    if (this.selectedStatusFilter.length === 0) {
      return t('filter-by-status');
    }
    return `${t('filter-by-status')} (${this.selectedStatusFilter.length})`;
  }

  toggleStatus(status: string) {
    if (this.selectedStatusFilter.includes(status)) {
      this.selectedStatusFilter = this.selectedStatusFilter.filter((s) => s !== status);
    } else {
      this.selectedStatusFilter = [...this.selectedStatusFilter, status];
    }
    this.dataSource.updateFilter(this.selectedStatusFilter);
  }

  isStatusSelected(status: string): boolean {
    return this.selectedStatusFilter.includes(status);
  }

  clearSearch() {
    this.searchText = '';
  }

  onSearch() {
    this.dataSource.fetchFirstPage(this.trimmedSearchText);
  }

  extendResultsTime() {
    this.isInternalLoading.set(true);
    this.configurationTesterService
      .extendResultsTime(this.selectedItem().id)
      .then((res) => {
        this.setExpirationDate(res.expirationDate);

        this.alertService.showToast(
          this.translocoService.translateObject(
            'configuration-tester-document-list-component.extend-test-results.extension-success'
          )
        );
      })
      .catch((err) => {
        this.alertService.showErrorToast(
          this.translocoService.translateObject(
            'configuration-tester-document-list-component.extend-test-results.extension-failed'
          ),
          err
        );
      })
      .finally(() => {
        this.isInternalLoading.set(false);
      });
  }

  clearResults() {
    const translations = this.translocoService.translateObject(
      'configuration-tester-document-list-component.clear-test-results',
      {
        content: {
          item: this.selectedItem().name,
        },
      }
    );

    this.dialogService
      .showWarningPopup({
        title: translations['title'],
        content: translations['content'],
        confirmTitle: translations['confirm-button'],
        confirmColor: SimpleDialogButtonColor.red,
      })
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          this.isInternalLoading.set(true);
          this.configurationTesterService
            .clearResults({ queryId: this.selectedItem().id })
            .then(() => {
              this.resultsCleared.emit(true);
              this.availableStatuses.emit([]);
              this.alertService.showToast(translations['delete-success']);
            })
            .catch((err) => {
              this.alertService.showErrorToast(translations['delete-failed'], err);
            })
            .finally(() => {
              this.isInternalLoading.set(false);
            });
        }
      });
  }

  exportResults(format: CTEvaluationResultExportFormat = 'csv') {
    this.alertService.showToast(
      this.translocoService.translateObject(
        'configuration-tester-document-list-component.export-test-results.export-started'
      )
    );
    this.isInternalLoading.set(true);
    this.configurationTesterService
      .exportResults(this.selectedItem().id, format)
      .then((res) => {
        saveAs(res, `QT-Results-${this.selectedItem().name}.${format}`);
      })
      .catch((err) => {
        this.alertService.showErrorToast(
          this.translocoService.translateObject(
            'configuration-tester-document-list-component.export-test-results.export-failed'
          ),
          err
        );
      })
      .finally(() => {
        this.isInternalLoading.set(false);
      });
  }

  toggleFullscreen() {
    this.fullscreenToggled.emit();
  }

  get fullscreenIcon(): string {
    return this.isFullscreen() ? 'invalid' : 'full_screen';
  }

  get fullscreenTooltip(): string {
    const exitKey = 'configuration-tester-document-list-component.exit-fullscreen';
    const enterKey = 'configuration-tester-document-list-component.enter-fullscreen';

    if (this.isFullscreen()) {
      return this.translocoService.translate(exitKey);
    } else {
      return this.translocoService.translate(enterKey);
    }
  }
}
